"use client";
import React from "react";
import Link from "components/common/Link";
import { MapPin, Clock, Phone, Star, ChevronRight } from "lucide-react";
import cx from "classnames";
import useOpenNow from "hooks/useOpenNow";
import Image from "components/common/Image";
import { BookingQRModal } from "components/common";
import { useT } from "providers/I18nProvider";

interface InformationProps {
  /** Giá trị "đang mở cửa" server tính sẵn theo giờ VN — làm state ban đầu để HTML có nhãn */
  initialOpenNow?: boolean | null;
  address?: string;
  displayedAddress?: string;
  oldStreetAddress?: string;
  isClosed?: boolean;
  phone?: string;
  openHour?: string;
  closeHour?: string;
  location?: [number, number]; // [longitude, latitude]
  style?: React.CSSProperties;
  className?: string;
  avatar?: {
    url: string;
    alt?: string;
  };
  name?: string;
  category?: string;
  /** Điểm đánh giá 0-10 */
  rating?: number;
  /** Cho phép đặt lịch (allowBooking từ BE) */
  allowBooking?: boolean;
  /** Ảnh shop — mobile dùng làm ảnh bìa vuốt ngang */
  gallery?: { url: string; alt?: string }[];
}

/**
 * Mobile: ảnh bìa vuốt ngang (scroll-snap) từ gallery, đếm 1/N + chấm.
 * Không có gallery → không render (dùng khung avatar cũ).
 */
const MobileCover = ({
  images,
  name,
  isClosed,
  closedLabel,
}: {
  images: { url: string; alt?: string }[];
  name: string;
  isClosed: boolean;
  closedLabel: string;
}) => {
  const [idx, setIdx] = React.useState(0);
  const trackRef = React.useRef<HTMLDivElement>(null);
  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    setIdx(Math.round(el.scrollLeft / Math.max(el.clientWidth, 1)));
  };
  if (!images.length) return null;
  return (
    <div className="md:hidden relative h-[240px] bg-gray-100">
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide"
      >
        {images.map((img, i) => (
          <div key={i} className="relative h-full w-full shrink-0 snap-center">
            <Image
              wrapClassName="absolute inset-0 w-full h-full"
              src={img.url}
              alt={img.alt || `${name} ${i + 1}`}
              priority={i === 0}
            />
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, i) => (
              <span
                key={i}
                className={cx(
                  "h-1.5 w-1.5 rounded-full",
                  i === idx ? "bg-white" : "bg-white/50",
                )}
              />
            ))}
          </div>
          <span className="absolute right-3 bottom-3 rounded-full bg-black/60 px-2.5 py-1 text-xs font-bold text-white">
            {idx + 1}/{images.length}
          </span>
        </>
      )}
      {isClosed && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <span className="text-white text-lg font-medium">{closedLabel}</span>
        </div>
      )}
    </div>
  );
};

const Information = ({
  address = "",
  displayedAddress = "",
  isClosed = false,
  phone = "",
  openHour = "",
  closeHour = "",
  style = {},
  className = "",
  avatar = { url: "", alt: "shop avatar" },
  name = "",
  category = "",
  rating = 0,
  allowBooking = true,
  location,
  gallery = [],
  initialOpenNow,
}: InformationProps) => {
  const t = useT();
  const [qrOpen, setQrOpen] = React.useState(false);
  const canBook = allowBooking && !isClosed;
  // Thanh CTA dính đáy (mobile) chỉ hiện khi cụm CTA chính đã cuộn khuất phía trên
  const ctaRef = React.useRef<HTMLDivElement>(null);
  const [showBar, setShowBar] = React.useState(false);
  React.useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    // Mobile: cụm CTA trong thân bị ẩn (display:none → rect 0) → thanh đáy luôn hiện
    const io = new IntersectionObserver(([e]) =>
      setShowBar(
        !e.isIntersecting &&
          (e.boundingClientRect.top < 0 || e.boundingClientRect.height === 0),
      )
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Đang mở cửa? Server tính sẵn theo giờ VN (initialOpenNow); hook cập nhật mỗi phút.
  const openNow = useOpenNow(openHour, closeHour, isClosed, initialOpenNow);

  const fullAddress = address || displayedAddress;
  const ratingText = rating ? Number(rating).toFixed(1) : "";
  const directionsUrl =
    Array.isArray(location) && location.length === 2
      ? `https://www.google.com/maps/dir/?api=1&destination=${location[1]},${location[0]}`
      : fullAddress
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
        : "";
  const coverImages = (gallery || []).filter((g) => g?.url).slice(0, 8);
  const hasCover = coverImages.length > 0;

  return (
    <>
    <MobileCover
      images={coverImages}
      name={name}
      isClosed={isClosed}
      closedLabel={t("shopInfo.closed")}
    />
    <div className="container mx-auto px-4 pt-0 md:pt-4 pb-4 md:pb-8 bg-white">
      {/* Breadcrumb (desktop) */}
      <nav
        aria-label="Breadcrumb"
        className="hidden md:flex items-center gap-1 text-sm text-gray-500 mb-4 flex-wrap"
      >
        <Link href="/" className="tap-area inline-flex min-h-6 items-center hover:text-primary">
          {t("breadcrumb.home")}
        </Link>
        <ChevronRight size={14} className="text-gray-300" />
        {category ? (
          <>
            <Link href="/search" className="tap-area inline-flex min-h-6 items-center hover:text-primary">
              {category}
            </Link>
            <ChevronRight size={14} className="text-gray-300" />
          </>
        ) : null}
        <span className="text-gray-700 font-medium line-clamp-1">{name}</span>
      </nav>

      <div
        className={cx(
          "grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6 items-start",
          className,
        )}
        style={style}
      >
        {/* Ảnh đại diện shop.
            Nhiều shop dùng LOGO vuông làm ảnh đại diện (vd Vyan Home Spa: logo
            959×960 nền trắng). Nếu cắt kiểu "cover" vào khung ngang thì logo bị
            xén mất chữ, nhìn rất khó hiểu. Cách xử lý: lấy chính ảnh đó phóng to
            + làm mờ làm nền, còn ảnh thật hiển thị TRỌN VẸN ở giữa. Ảnh ngang
            bình thường vẫn đẹp, ảnh vuông/logo thì không còn bị cắt. */}
        <div
          className={cx(
            "relative h-[180px] sm:h-[320px] rounded-2xl overflow-hidden bg-gray-50 border border-gray-100",
            hasCover ? "max-md:hidden" : "max-md:mt-4",
          )}
        >
          <div className="absolute inset-0 scale-110 blur-2xl opacity-45">
            <Image
              wrapClassName="w-full h-full"
              src={avatar?.url}
              alt=""
              aria-hidden="true"
              priority={false}
            />
          </div>
          <Image
            wrapClassName="absolute inset-0 w-full h-full"
            className="relative"
            objectFit="contain"
            src={avatar?.url}
            alt={avatar?.alt || name || "shop avatar"}
            priority
          />
          {isClosed && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white text-lg font-medium">
                {t("shopInfo.closed")}
              </span>
            </div>
          )}
        </div>

        {/* Thông tin */}
        <div className="relative flex flex-col">
          {/* Mobile: avatar 60px đè lên mép ảnh bìa */}
          {hasCover && avatar?.url && (
            <div className="md:hidden -mt-[30px] mb-2 h-[60px] w-[60px] overflow-hidden rounded-2xl border-[3px] border-white bg-white shadow-[0_4px_12px_rgba(0,0,0,0.12)]">
              <Image
                wrapClassName="w-full h-full"
                objectFit="cover"
                src={avatar.url}
                alt={avatar?.alt || name}
              />
            </div>
          )}
          <h1 className="text-[22px] md:text-[28px] font-extrabold text-black01 !mb-2 leading-tight">
            {name}
          </h1>

          {/* Hàng chip: điểm + danh mục + trạng thái mở cửa */}
          <div className="flex items-center gap-2 flex-wrap mb-3 md:mb-4">
            {rating > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1">
                <Star size={14} className="fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-bold text-primary">
                  {ratingText}
                </span>
                <span className="text-xs text-gray-500">/10</span>
              </span>
            )}
            {category && (
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                {category}
              </span>
            )}
            {openNow !== null && (
              <span
                className={cx(
                  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold",
                  openNow
                    ? "bg-green-50 text-green-600"
                    : "bg-orange-50 text-orange-500",
                )}
              >
                <span
                  className={cx(
                    "h-1.5 w-1.5 rounded-full",
                    openNow ? "bg-green-500" : "bg-orange-400",
                  )}
                />
                {openNow ? t("shopInfo.openNow") : t("shopInfo.closedNow")}
              </span>
            )}
          </div>

          {/* Thông tin liên hệ (số điện thoại chuyển thành nút "Gọi tiệm" ở dưới,
              không lặp lại ở đây) */}
          <div className="flex flex-col gap-2 md:gap-2.5 text-[14px] md:text-[15px]">
            {fullAddress && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 flex-1 min-w-0">{fullAddress}</span>
                {directionsUrl && (
                  <a
                    href={directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="md:hidden tap-area inline-flex min-h-6 shrink-0 items-center text-sm font-bold text-primary"
                  >
                    {t("shopInfo.directions")}
                  </a>
                )}
              </div>
            )}
            {(openHour || closeHour) && (
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <span className="text-gray-700">
                  {openHour}
                  {openHour && closeHour && " - "}
                  {closeHour}
                </span>
              </div>
            )}
          </div>

          {/* Cụm đặt lịch: tách thành dải riêng có đường kẻ, nằm ngay trong khối
              thông tin để khách thấy CTA không cần cuộn (chốt với chị Naomi 30/09).
              Không có nút bản đồ. */}
          {allowBooking && !isClosed && (
            <p className="md:hidden !mb-0 mt-2.5 text-[12px] text-gray-500">
              {t("shopInfo.policyNote")}
            </p>
          )}
          {/* Mobile: bỏ cụm nút trong thân (trùng thanh dính đáy) */}
          <div ref={ctaRef} className="hidden md:block mt-5 pt-5 border-t border-gray-100">
            <div className="flex gap-3">
              {allowBooking && !isClosed && (
                <button
                  type="button"
                  onClick={() => setQrOpen(true)}
                  className="flex-[2] max-w-[280px] rounded-full bg-primary px-6 py-3 text-base font-semibold text-white transition-opacity hover:opacity-90"
                >
                  {t("shopInfo.bookNow")}
                </button>
              )}
              {phone && (
                <a
                  href={`tel:${String(phone).replace(/[^\d+]/g, "")}`}
                  className="flex-1 max-w-[180px] inline-flex items-center justify-center gap-2 rounded-full border-[1.5px] border-primary bg-white whitespace-nowrap px-4 py-3 text-sm font-semibold text-primary transition-colors hover:bg-primary/5"
                >
                  <Phone size={16} />
                  <span>{t("shopInfo.callShop")}</span>
                </a>
              )}
            </div>
            {allowBooking && !isClosed && (
              <p className="!mb-0 mt-2.5 text-[12.5px] text-gray-500">
                {t("shopInfo.policyNote")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile: khi cụm CTA trên cuộn khuất → thanh Đặt lịch/Gọi dính đáy màn hình.
          Trên điện thoại "Đặt lịch" mở thẳng store (QR vô nghĩa trên chính máy). */}
      {(canBook || phone) && (
        <div
          className={`md:hidden fixed inset-x-0 bottom-0 z-[900] border-t border-gray-100 bg-white/95 backdrop-blur px-4 pt-2.5 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] transition-transform duration-200 motion-reduce:transition-none ${
            showBar ? "translate-y-0" : "translate-y-full"
          }`}
          style={{ paddingBottom: "calc(10px + env(safe-area-inset-bottom))" }}
          aria-hidden={!showBar}
          // inert: khi thanh trượt xuống ẩn, nút bên trong không còn nhận focus/Tab (aria-hidden chỉ ẩn với screen reader)
          inert={!showBar}
        >
          <div className="flex gap-2.5">
            {phone && (
              <a
                href={`tel:${String(phone).replace(/[^\d+]/g, "")}`}
                aria-label={t("shopInfo.callShop")}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-[1.5px] border-primary text-primary"
              >
                <Phone size={20} />
              </a>
            )}
            {canBook && (
              <button
                type="button"
                onClick={() => setQrOpen(true)}
                className="flex h-12 flex-1 items-center justify-center rounded-full text-[16px] font-semibold"
                style={{ backgroundColor: "var(--primary)", color: "#fff" }}
              >
                {t("shopInfo.bookNow")}
              </button>
            )}
          </div>
        </div>
      )}

      <BookingQRModal open={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
    </>
  );
};

export default Information;
